import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { useFeatureFlagEnabled } from "posthog-js/react";
import CONFIG from "config/config";
import { authorization } from "module/auth";

// PostHog flag gating Import SVG (staging/test users only; never shown in production builds).
export const SVG_IMPORT_FLAG = "builder_svg_import";
const SVG_IMPORT_FORCE_KEY = "builder_svg_import_force";

const readForceOverride = (): boolean => {
  try {
    return window.localStorage.getItem(SVG_IMPORT_FORCE_KEY) === "on";
  } catch {
    return false; // storage unavailable
  }
};

const isPosthogLoaded = (): boolean => Boolean((posthog as unknown as { __loaded?: boolean }).__loaded);

// The /design/... builder route never initialises posthog-js; ask the flags endpoint directly
// for the signed-in user (the app identifies users by their id) instead of starting analytics there.
const fetchFlag = async (): Promise<boolean> => {
  const userId = authorization.getUser()?.id;
  if (!userId || !CONFIG.POSTHOG_KEY || !CONFIG.POSTHOG_HOST) return false;
  const response = await fetch(`${CONFIG.POSTHOG_HOST}/flags/?v=2`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ api_key: CONFIG.POSTHOG_KEY, distinct_id: String(userId) }),
  });
  if (!response.ok) return false;
  const body = await response.json();
  return body?.flags?.[SVG_IMPORT_FLAG]?.enabled === true;
};

/** Flag on (or the non-prod QA override set), and never in a production build. */
export const useSvgImportEnabled = (): boolean => {
  const isProduction = CONFIG.ENV === "production";
  const sdkFlag = useFeatureFlagEnabled(SVG_IMPORT_FLAG) === true;
  const [fetchedFlag, setFetchedFlag] = useState(false);

  useEffect(() => {
    if (isProduction || isPosthogLoaded()) return undefined;
    let cancelled = false;
    fetchFlag()
      .then((enabled) => !cancelled && setFetchedFlag(enabled))
      .catch((error) => console.warn("SVG import: could not read the feature flag", error));
    return () => {
      cancelled = true;
    };
  }, [isProduction]);

  if (isProduction) return false;
  return sdkFlag || fetchedFlag || readForceOverride();
};
